import React from "react";
import { motion } from "framer-motion";
import { marked } from "marked";
import DOMPurify from "dompurify";
import dayjs from "dayjs";

const FloatingChatMessage = ({ message, isStreaming }) => {
  const isUser = message.sender === "user";

  // Convert Markdown → HTML → Sanitized
  const rawHtml = marked(message.content || "", {
    breaks: true,
    gfm: true,
  });
  const cleanHtml = DOMPurify.sanitize(rawHtml);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
    >
      <style>
        {`
          .floating-markdown-body ul {
            list-style-type: disc !important;
            padding-left: 1.25rem;
          }
          .floating-markdown-body ul ul {
            list-style-type: circle !important;
          }
          .floating-markdown-body ol ul {
            list-style-type: circle !important;
          }
          .floating-markdown-body ol {
            list-style-type: decimal !important;
            padding-left: 1.25rem;
          }
          .floating-markdown-body {
            background-color: transparent;
            color: #1f2937;
            font-size: 14px;
          }
          .floating-markdown-body h1,
          .floating-markdown-body h2,
          .floating-markdown-body h3,
          .floating-markdown-body h4,
          .floating-markdown-body h5,
          .floating-markdown-body h6 {
            font-size: 1em;
            font-weight: 600;
            margin: 0.5em 0;
            color: #1f2937;
          }
          .floating-markdown-body p {
            margin: 0.5em 0;
            color: #1f2937;
          }
          .floating-markdown-body code {
            background: #f3f4f6;
            padding: 0.2em 0.4em;
            border-radius: 4px;
            font-size: 0.9em;
            color: #1f2937;
          }
          .floating-markdown-body pre {
            background: #f3f4f6;
            padding: 0.8em;
            border-radius: 6px;
            overflow-x: auto;
            font-size: 0.85em;
          }
          .floating-markdown-body pre code {
            background: transparent;
            color: #1f2937;
          }
          .dark .floating-markdown-body {
            color: #f9fafb;
          }
          .dark .floating-markdown-body h1,
          .dark .floating-markdown-body h2,
          .dark .floating-markdown-body h3,
          .dark .floating-markdown-body h4,
          .dark .floating-markdown-body h5,
          .dark .floating-markdown-body h6 {
            color: #f9fafb;
          }
          .dark .floating-markdown-body p {
            color: #f9fafb;
          }
          .dark .floating-markdown-body code {
            background: #374151;
            color: #f9fafb;
          }
          .dark .floating-markdown-body pre {
            background: #374151;
          }
          .dark .floating-markdown-body pre code {
            background: transparent;
            color: #f9fafb;
          }
          .dark .floating-markdown-body li {
            color: #f9fafb;
          }
          .dark .floating-markdown-body strong {
            color: #f9fafb;
          }
          .dark .floating-markdown-body a {
            color: #60a5fa;
          }
        `}
      </style>

      <div
        className={`max-w-[85%] rounded-2xl py-2.5 px-3 ${
          isUser
            ? "bg-skin-button-base text-white rounded-br-md"
            : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md"
        } ${isStreaming ? "animate-pulse" : ""}`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words text-sm">
            {message.content}
          </p>
        ) : (
          <div
            className="floating-markdown-body"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        )}

        <div
          className={`text-xs mt-1 opacity-75 ${
            isUser ? "text-white" : "text-gray-500 dark:text-gray-300"
          }`}
        >
          {dayjs(message.timestamp).format("HH:mm")}
        </div>
      </div>
    </motion.div>
  );
};

export default FloatingChatMessage;
